<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import * as THREE from 'three';

const emit = defineEmits(['exportImage']);

const props = defineProps({
    diameter: Number,
    height: Number,
    textureUrl: String,
    sideView: String,
    rotate: Boolean,
    rotationSpeed: {
        type: Number,
        default: 0.01,
    },
});
const container = ref(null);
let scene = null;
let camera = null;
let renderer = null;
let cylinder = null;
const animationFrame = ref(null);
const startUv = ref(props.sideView === 'front' ? 0.75 : 0.25);

const init3DScene = () => {
    const canvasWidth = props.diameter;
    const canvasHeight = props.height;

    scene = new THREE.Scene();

    camera = new THREE.OrthographicCamera(
        -props.diameter / 2,
        props.diameter / 2,
        props.height / 2,
        -props.height / 2,
        0.1,
        1000
    );
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);

    renderer = new THREE.WebGLRenderer({
        alpha: false,
        antialias: true,
        powerPreference: 'default',
        failIfMajorPerformanceCaveat: false,
        preserveDrawingBuffer: true
    });
    // Ensure correct color management for textures
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(canvasWidth, canvasHeight);
    container.value.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(
        props.textureUrl,
        (texture) => {
            // Treat the PNG as an sRGB color texture to avoid washed-out colors
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.flipY = false;
            texture.center.set(0.5, 0.5);
            texture.rotation = Math.PI;
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.ClampToEdgeWrapping;
            texture.repeat.set(1, 1);
            texture.offset.x = startUv.value;

            const material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.DoubleSide,
                transparent: true,
                // MeshBasicMaterial ignores lights; keep tone mapping disabled
                toneMapped: false,
            });

            const radius = props.diameter / 2;
            const geometry = new THREE.CylinderGeometry(
                radius,
                radius,
                props.height,
                64,
                1,
                true
            );

            cylinder = new THREE.Mesh(geometry, material);
            scene.add(cylinder);

            if (props.rotate) {
                animate();
            } else {
                renderer.render(scene, camera);
                exportImage();
            }

            console.log(renderer.capabilities.isWebGL2 ? 'WebGL2' : 'WebGL1');
        },
        undefined,
        (error) => {
            console.error('Fehler beim Laden der Textur:', error);
        }
    );
};

const animate = () => {
    animationFrame.value = requestAnimationFrame(animate);

    if (cylinder) {
        cylinder.rotation.y += props.rotationSpeed;
    }

    renderer.render(scene, camera);
};

const disposeScene = () => {
    cancelAnimationFrame(animationFrame.value);
    scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
            obj.geometry.dispose();
            if (obj.material.map) obj.material.map.dispose();
            obj.material.dispose();
        }
    });
    renderer.dispose();
    container.value.innerHTML = '';
};

onMounted(() => {
    init3DScene();
});

onBeforeUnmount(() => {
    disposeScene();
});

//Export the rendered image as a canvas to create complete preview images
function exportImage() {
    const glCanvas = renderer.domElement;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = props.diameter;
    tempCanvas.height = props.height;

    const ctx = tempCanvas.getContext('2d');
    ctx.drawImage(glCanvas, 0, 0);

    emit('exportImage', tempCanvas);

    disposeScene();
}
</script>

<style scoped>
.bottle-print-area-preview-container {
    position: relative;
    width: auto;
    height: auto;
}
</style>

<template>
    <div ref="container" class="bottle-print-area-preview-container"></div>
</template>
